import { supabase } from '../lib/supabase';

interface PrestaShopConfig {
  shopUrl: string;
  apiKey: string;
}

interface PrestaShopProduct {
  id: number;
  name: { [key: string]: string };
  description: { [key: string]: string };
  price: string;
  wholesale_price: string;
  reference: string;
  quantity: number;
  active: string;
  categories: number[];
  images: { [key: string]: string };
  tags: string[];
}

export class PrestaShopService {
  private config: PrestaShopConfig;
  private chatbotPublicId: string;

  constructor(config: PrestaShopConfig, chatbotPublicId: string) {
    this.config = config;
    this.chatbotPublicId = chatbotPublicId;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Ensure the URL ends with a slash if needed
    const baseUrl = this.config.shopUrl.endsWith('/') 
      ? this.config.shopUrl 
      : `${this.config.shopUrl}/`;
      
    const url = `${baseUrl}api${endpoint}`;
    
    // Create Basic Auth header from API key
    const authHeader = `Basic ${btoa(this.config.apiKey + ':')}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      throw new Error(`PrestaShop API error: ${response.statusText}`);
    }

    return response.json();
  }

  async syncProducts(): Promise<void> {
    try {
      // Get list of products
      const response = await this.makeRequest<{
        products: { product: PrestaShopProduct[] };
      }>('/products?output_format=JSON&display=full');

      // Process each product
      for (const product of response.products.product) {
        await this.upsertProduct(product);
      }
    } catch (err) {
      console.error('Error syncing PrestaShop products:', err);
      throw err;
    }
  }

  private async upsertProduct(prestaProduct: PrestaShopProduct): Promise<void> {
    try {
      // Extract product name and description from language object
      // Usually the first language is the default one
      const name = prestaProduct.name[1] || Object.values(prestaProduct.name)[0];
      const description = prestaProduct.description[1] || Object.values(prestaProduct.description)[0];
      
      // Get image URL if available
      let imageUrl = null;
      if (prestaProduct.images && Object.keys(prestaProduct.images).length > 0) {
        // The image URL format depends on PrestaShop's configuration
        // This is a common format but might need adjustment
        const imageId = Object.keys(prestaProduct.images)[0];
        imageUrl = `${this.config.shopUrl}/img/p/${imageId}.jpg`;
      }

      // Calculate position based on existing products
      const { data: existingProducts } = await supabase
        .from('products')
        .select('position')
        .eq('chatbot_public_id', this.chatbotPublicId)
        .order('position', { ascending: false })
        .limit(1);

      const maxPosition = existingProducts && existingProducts.length > 0 
        ? existingProducts[0].position + 1 
        : 0;

      const { error } = await supabase
        .from('products')
        .upsert({
          chatbot_public_id: this.chatbotPublicId,
          name: name,
          description: description,
          price: parseFloat(prestaProduct.price),
          currency: 'EUR', // PrestaShop typically uses EUR
          image_url: imageUrl,
          sku: prestaProduct.reference,
          stock: parseInt(prestaProduct.quantity.toString()),
          source: 'prestashop',
          external_id: prestaProduct.id.toString(),
          position: maxPosition,
          metadata: {
            active: prestaProduct.active === '1',
            wholesale_price: prestaProduct.wholesale_price,
            categories: prestaProduct.categories
          }
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error upserting product:', err);
      throw err;
    }
  }

  async handleWebhook(action: string, payload: any): Promise<void> {
    try {
      switch (action) {
        case 'product_add':
        case 'product_update': {
          const productId = payload.id;
          const product = await this.makeRequest<{
            product: PrestaShopProduct;
          }>(`/products/${productId}?output_format=JSON`);
          await this.upsertProduct(product.product);
          break;
        }
        case 'product_delete': {
          const { error } = await supabase
            .from('products')
            .delete()
            .eq('external_id', payload.id.toString())
            .eq('chatbot_public_id', this.chatbotPublicId);

          if (error) throw error;
          break;
        }
      }
    } catch (err) {
      console.error('Error handling PrestaShop webhook:', err);
      throw err;
    }
  }

  async verifyWebhook(payload: any, signature: string): Promise<boolean> {
    try {
      const message = JSON.stringify(payload);
      const generated = await this.generateSignature(message);
      return signature === generated;
    } catch (err) {
      console.error('Error verifying PrestaShop webhook:', err);
      return false;
    }
  }

  private async generateSignature(message: string): Promise<string> {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(this.config.apiKey),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign(
      'HMAC',
      key,
      encoder.encode(message)
    );

    return Array.from(new Uint8Array(signature))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  async registerWebhook(action: string, url: string): Promise<void> {
    try {
      // PrestaShop doesn't have a standard API for webhook registration
      // This would typically be done through a custom module
      // For now, we'll just log the request
      console.log(`Would register PrestaShop webhook for ${action} to ${url}`);
      
      // In a real implementation, you might make a request to a custom endpoint:
      /*
      const response = await this.makeRequest('/webhooks', {
        method: 'POST',
        body: JSON.stringify({
          action,
          url,
          secret: this.config.apiKey
        })
      });
      */
    } catch (err) {
      console.error(`Error registering PrestaShop webhook for ${action}:`, err);
      throw err;
    }
  }
}